import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { Toaster } from "sonner";
import { motion } from "framer-motion";

// Import your pages
import HomePage from "./pages/HomePage";
import ChatPage from "./pages/ChatPage";
import { useAppStore } from "./store/useAppStore";

// Error Fallback Component
const ErrorFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="min-h-screen flex items-center justify-center bg-slate-900 p-4"
  >
    <div className="max-w-md w-full bg-red-500/10 border border-red-500/20 rounded-2xl p-6 text-center backdrop-blur-sm">
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
        <span className="text-red-400 text-xl">⚠️</span>
      </div>
      <h2 className="text-lg font-semibold text-white mb-2">
        Something went wrong
      </h2>
      <p className="text-slate-400 text-sm mb-4">{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
      >
        Try again
      </button>
    </div>
  </motion.div>
);

// Loading Component
const LoadingSpinner = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="min-h-screen flex items-center justify-center bg-slate-900"
  >
    <div className="text-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        className="w-12 h-12 mx-auto mb-4 border-4 border-blue-500/30 border-t-blue-500 rounded-full"
      />
      <p className="text-slate-400">Loading Voice AI Assistant...</p>
    </div>
  </motion.div>
);

const App: React.FC = () => {
  const settings = useAppStore((state) => state.settings);
  const [isLoading, setIsLoading] = React.useState(true);

  // Initialize app
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Show loading screen during initialization
  if (isLoading) return <LoadingSpinner />;

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Router>
        <div className="min-h-screen bg-slate-900 text-white">
          <Routes>
            {/* Home Page - Landing page with all sections */}
            <Route path="/" element={<HomePage />} />

            {/* Chat Page - Voice AI interface */}
            <Route path="/chat" element={<ChatPage />} />

            {/* 404 Not Found */}
            <Route
              path="*"
              element={
                <div className="min-h-screen flex items-center justify-center bg-slate-900">
                  <div className="text-center">
                    <h1 className="text-6xl font-bold text-blue-400 mb-4">
                      404
                    </h1>
                    <p className="text-xl text-slate-300 mb-8">
                      Page not found
                    </p>
                    <a
                      href="/"
                      className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 rounded-lg hover:shadow-lg transition-all"
                    >
                      Go Home
                    </a>
                  </div>
                </div>
              }
            />
          </Routes>

          {/* Toast Notifications */}
          <Toaster
            theme={settings.theme}
            position="bottom-right"
            expand={true}
            richColors
            toastOptions={{
              style: {
                background: settings.theme === "dark" ? "#1e293b" : "#ffffff",
                color: settings.theme === "dark" ? "#f1f5f9" : "#0f172a",
                border: `1px solid ${
                  settings.theme === "dark" ? "#475569" : "#e2e8f0"
                }`,
              },
            }}
          />
        </div>
      </Router>
    </ErrorBoundary>
  );
};

export default App;